import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Close, FilterList, Refresh } from "@mui/icons-material";
import { useCallback, useMemo, useRef, useEffect, useState } from "react";

import { FilterFieldConfig, FilterOption } from "src/config/filterConfig";
import { AppliedFilter } from "src/utils/filterUrlUtils";
import FilterSection from "./FilterSection";
import MultiSelectFilter from "./MultiSelectFilter";
import RangeFilter from "./RangeFilter";
import DateRangeFilter from "./DateRangeFilter";
import TextFilter, { TextFilterMode } from "./TextFilter";
import BooleanFilter from "./BooleanFilter";
import HistogramFilter from "./HistogramFilter";

interface FacetedFilterSidebarProps {
  /** Filter configuration for this dataset */
  filterConfig: FilterFieldConfig[];

  /** Currently applied filters */
  appliedFilters: AppliedFilter[];

  /** Facet data with counts for multiSelect filters */
  facetData?: Record<string, FilterOption[]>;

  /** Callback when a filter value is toggled (for multiSelect) */
  onToggleValue: (fieldName: string, value: string) => void;

  /** Callback to set range filter */
  onSetRange: (fieldName: string, min?: number, max?: number) => void;

  /** Callback to set date range filter */
  onSetDateRange: (fieldName: string, after?: string, before?: string) => void;

  /** Callback to set text filter */
  onSetText: (fieldName: string, value: string, mode: TextFilterMode) => void;

  /** Callback to set boolean filter */
  onSetBoolean: (fieldName: string, value: boolean | undefined) => void;

  /** Callback to remove all filters for a field */
  onRemoveFieldFilters: (fieldName: string) => void;

  /** Callback to clear all filters */
  onClearAll: () => void;

  /** Callback to close the sidebar */
  onClose?: () => void;

  /** Loading state */
  isLoading?: boolean;

  /** Whether to show the header */
  showHeader?: boolean;

  /** Title for the header */
  title?: string;

  /** Whether facet data is loading */
  isFacetLoading?: boolean;

  /** Callback to refresh facet data */
  onRefreshFacets?: () => void;
}

/**
 * Amazon-style faceted filter sidebar
 *
 * Features:
 * - Collapsible filter sections
 * - Multiple filter types (multiSelect, range, date, text, boolean)
 * - Badge showing active filter count per section
 * - Clear all button
 * - Facet counts from server
 * - Loading states
 */
const FacetedFilterSidebar: React.FC<FacetedFilterSidebarProps> = ({
  filterConfig,
  appliedFilters,
  facetData = {},
  onToggleValue,
  onSetRange,
  onSetDateRange,
  onSetText,
  onSetBoolean,
  onRemoveFieldFilters,
  onClearAll,
  onClose,
  isLoading = false,
  showHeader = true,
  title = "Filters",
  isFacetLoading = false,
  onRefreshFacets,
}) => {
  const theme = useTheme();

  // Track text filter mode (contains vs exact) per field
  const [textFilterModes, setTextFilterModes] = useState<
    Record<string, TextFilterMode>
  >({});

  const getTextFilterMode = (fieldName: string): TextFilterMode =>
    textFilterModes[fieldName] ?? "contains";

  const setTextFilterMode = useCallback(
    (fieldName: string, mode: TextFilterMode) => {
      setTextFilterModes((prev) => ({ ...prev, [fieldName]: mode }));
    },
    [],
  );

  // Stable callback refs to prevent unnecessary re-renders in child components
  const onSetTextRef = useRef(onSetText);
  const onSetBooleanRef = useRef(onSetBoolean);
  const onSetRangeRef = useRef(onSetRange);
  const onSetDateRangeRef = useRef(onSetDateRange);
  const onToggleValueRef = useRef(onToggleValue);
  const onRemoveFieldFiltersRef = useRef(onRemoveFieldFilters);

  // Keep refs up to date
  useEffect(() => {
    onSetTextRef.current = onSetText;
    onSetBooleanRef.current = onSetBoolean;
    onSetRangeRef.current = onSetRange;
    onSetDateRangeRef.current = onSetDateRange;
    onToggleValueRef.current = onToggleValue;
    onRemoveFieldFiltersRef.current = onRemoveFieldFilters;
  }, [
    onSetText,
    onSetBoolean,
    onSetRange,
    onSetDateRange,
    onToggleValue,
    onRemoveFieldFilters,
  ]);

  // Stable callbacks that use refs
  const handleSetText = useCallback(
    (fieldName: string, value: string, mode: TextFilterMode = "contains") => {
      onSetTextRef.current(fieldName, value, mode);
    },
    [],
  );

  const handleSetBoolean = useCallback(
    (fieldName: string, value: boolean | undefined) => {
      onSetBooleanRef.current(fieldName, value);
    },
    [],
  );

  const handleSetRange = useCallback(
    (fieldName: string, min?: number, max?: number) => {
      onSetRangeRef.current(fieldName, min, max);
    },
    [],
  );

  const handleSetDateRange = useCallback(
    (fieldName: string, after?: string, before?: string) => {
      onSetDateRangeRef.current(fieldName, after, before);
    },
    [],
  );

  const handleToggleValue = useCallback((fieldName: string, value: string) => {
    onToggleValueRef.current(fieldName, value);
  }, []);

  const handleRemoveFieldFilters = useCallback((fieldName: string) => {
    onRemoveFieldFiltersRef.current(fieldName);
  }, []);

  // Get active filter count for a field
  const getActiveCount = (fieldName: string): number => {
    return appliedFilters
      .filter((f) => f.fieldName === fieldName)
      .reduce((count, f) => {
        // Multi-value filters (e.g., ["AREVA", "NRC"]) count each selected value
        return count + (Array.isArray(f.value) ? f.value.length : 1);
      }, 0);
  };

  // Get selected values for a multiSelect field
  const getSelectedValues = (fieldName: string): string[] => {
    const filter = appliedFilters.find(
      (f) => f.fieldName === fieldName && f.operator === "eq",
    );
    if (!filter) return [];
    return Array.isArray(filter.value) ? filter.value : [filter.value];
  };

  // Get range values for a range field
  const getRangeValue = (fieldName: string) => {
    const minFilter = appliedFilters.find(
      (f) => f.fieldName === fieldName && f.operator === "ge",
    );
    const maxFilter = appliedFilters.find(
      (f) => f.fieldName === fieldName && f.operator === "le",
    );

    if (!minFilter && !maxFilter) return undefined;

    return {
      min: minFilter ? Number(minFilter.value) : undefined,
      max: maxFilter ? Number(maxFilter.value) : undefined,
    };
  };

  // Get date range values
  const getDateRangeValue = (fieldName: string) => {
    const afterFilter = appliedFilters.find(
      (f) => f.fieldName === fieldName && f.operator === "ge",
    );
    const beforeFilter = appliedFilters.find(
      (f) => f.fieldName === fieldName && f.operator === "le",
    );

    if (!afterFilter && !beforeFilter) return undefined;

    return {
      after: afterFilter ? String(afterFilter.value) : undefined,
      before: beforeFilter ? String(beforeFilter.value) : undefined,
    };
  };

  // Get text value (check both like and eq operators for contains/exact modes)
  const getTextValue = (fieldName: string): string => {
    const filter = appliedFilters.find(
      (f) =>
        f.fieldName === fieldName &&
        (f.operator === "like" || f.operator === "eq"),
    );
    return filter ? String(filter.value) : "";
  };

  // Get boolean value
  const getBooleanValue = (fieldName: string): boolean | undefined => {
    const filter = appliedFilters.find(
      (f) => f.fieldName === fieldName && f.operator === "eq",
    );
    if (!filter) return undefined;
    // Value is always a string in AppliedFilter
    return filter.value === "true";
  };

  // Sort and group configs
  const sortedConfig = useMemo(() => {
    return [...filterConfig].sort(
      (a, b) => (a.order ?? 999) - (b.order ?? 999),
    );
  }, [filterConfig]);

  const hasActiveFilters = appliedFilters.length > 0;

  // Render the appropriate filter component based on type
  const renderFilterContent = (config: FilterFieldConfig) => {
    const options = config.options || facetData[config.fieldName] || [];

    switch (config.type) {
      case "multiSelect":
        return (
          <MultiSelectFilter
            options={options}
            selectedValues={getSelectedValues(config.fieldName)}
            onChange={(values) => {
              // Toggle individual values
              const currentValues = getSelectedValues(config.fieldName);
              values.forEach((value) => {
                if (!currentValues.includes(value)) {
                  handleToggleValue(config.fieldName, value);
                }
              });
              currentValues.forEach((value) => {
                if (!values.includes(value)) {
                  handleToggleValue(config.fieldName, value);
                }
              });
            }}
            maxVisible={config.maxVisibleOptions ?? 5}
            searchable={config.searchable ?? options.length > 10}
            showCounts={config.showCounts ?? true}
            isLoading={isFacetLoading && options.length === 0}
            searchPlaceholder={`Search ${config.label.toLowerCase()}...`}
          />
        );

      case "select":
        return (
          <MultiSelectFilter
            options={options}
            selectedValues={getSelectedValues(config.fieldName)}
            onChange={(values) => {
              // Single select - only keep the last value
              const newValue = values[values.length - 1];
              if (newValue) {
                handleRemoveFieldFilters(config.fieldName);
                handleToggleValue(config.fieldName, newValue);
              } else {
                handleRemoveFieldFilters(config.fieldName);
              }
            }}
            maxVisible={config.maxVisibleOptions ?? 10}
            showCounts={config.showCounts ?? true}
            isLoading={isFacetLoading && options.length === 0}
          />
        );

      case "range":
        return (
          <RangeFilter
            value={getRangeValue(config.fieldName)}
            onChange={(range) => {
              handleSetRange(config.fieldName, range?.min, range?.max);
            }}
            config={config.rangeConfig}
            showInputs={true}
            debounceMs={1200}
          />
        );

      case "dateRange":
        return (
          <DateRangeFilter
            value={getDateRangeValue(config.fieldName)}
            onChange={(range) => {
              handleSetDateRange(config.fieldName, range?.after, range?.before);
            }}
            presets={config.datePresets}
            showCustomRange={true}
          />
        );

      case "text":
        return (
          <TextFilter
            value={getTextValue(config.fieldName)}
            onChange={(value) =>
              handleSetText(
                config.fieldName,
                value,
                getTextFilterMode(config.fieldName),
              )
            }
            placeholder={
              config.placeholder ?? `Search ${config.label.toLowerCase()}...`
            }
            debounceMs={3000}
            mode={getTextFilterMode(config.fieldName)}
            onModeChange={(mode) => setTextFilterMode(config.fieldName, mode)}
          />
        );

      case "boolean":
        return (
          <BooleanFilter
            value={getBooleanValue(config.fieldName)}
            onChange={(value) => handleSetBoolean(config.fieldName, value)}
            showAnyOption={true}
          />
        );

      case "histogram":
        // Convert facet options to histogram buckets
        const histogramBuckets = options.map((opt) => ({
          label: opt.label,
          count: opt.count ?? 0,
        }));
        return (
          <HistogramFilter
            buckets={histogramBuckets}
            showCounts={false}
            compact={true}
            isLoading={isFacetLoading && options.length === 0}
            rangeValue={getRangeValue(config.fieldName)}
            onRangeChange={(range) => {
              handleSetRange(config.fieldName, range?.min, range?.max);
            }}
          />
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 200,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0, // Critical for flex children to enable scrolling
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* Header */}
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            backdropFilter: "blur(8px)",
            flexShrink: 0, // Prevent header from shrinking
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterList
              sx={{
                fontSize: "1.25rem",
                color: theme.palette.primary.main,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              {title}
            </Typography>
            {hasActiveFilters && (
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  borderRadius: "10px",
                  px: 1,
                  py: 0.25,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                }}
              >
                {appliedFilters.length}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {onRefreshFacets && (
              <IconButton
                size="small"
                onClick={onRefreshFacets}
                disabled={isFacetLoading}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <Refresh
                  sx={{
                    fontSize: "1.125rem",
                    animation: isFacetLoading
                      ? "spin 1s linear infinite"
                      : "none",
                    "@keyframes spin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            )}
            {onClose && (
              <IconButton
                size="small"
                onClick={onClose}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.text.primary,
                  },
                }}
              >
                <Close sx={{ fontSize: "1.125rem" }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {/* Clear all button (when filters active) */}
      {hasActiveFilters && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0, // Prevent from shrinking
          }}
        >
          <Button
            size="small"
            onClick={onClearAll}
            sx={{
              fontSize: "0.75rem",
              color: theme.palette.error.main,
              textTransform: "none",
              py: 0.25,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
              },
            }}
          >
            Clear all filters
          </Button>
        </Box>
      )}

      {/* Filter sections */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0, // Critical for nested flex scrolling
          overflowY: "auto",
          px: 2,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.secondary, 0.2),
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: alpha(theme.palette.text.secondary, 0.3),
            },
          },
        }}
      >
        {sortedConfig.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              No filters available
            </Typography>
          </Box>
        ) : (
          sortedConfig.map((config, index) => (
            <FilterSection
              key={config.fieldName}
              title={config.label}
              defaultExpanded={config.defaultExpanded ?? index < 3}
              activeCount={getActiveCount(config.fieldName)}
              collapsible={!config.alwaysVisible}
            >
              {renderFilterContent(config)}
            </FilterSection>
          ))
        )}
      </Box>
    </Box>
  );
};

export default FacetedFilterSidebar;
